/**
 * Service to manage the last used delivery address in localStorage
 * This ensures that users see their previously selected address in new orders
 * instead of always defaulting to the institute's default address
 *
 * The stored record is scoped to the institute it was chosen in, and a stored id
 * is only usable when it still belongs to the current institute's ship-to list.
 */

const LAST_USED_ADDRESS_KEY = 'lastUsedDeliveryAddress'

export interface LastUsedAddressData {
  shippingAddressId: string
  instituteId: string
  timestamp: number
  userId?: string // Optional: to handle multi-user scenarios
}

const normaliseId = (
  value: string | number | null | undefined
): string | null => {
  if (value === null || value === undefined) {
    return null
  }
  const normalised = String(value).trim()
  return normalised.length > 0 ? normalised : null
}

/**
 * Save the last used delivery address to localStorage
 */
export const saveLastUsedAddress = (
  shippingAddressId: string | number,
  instituteId: string | number | null | undefined,
  userId?: string
): void => {
  try {
    const normalisedInstituteId = normaliseId(instituteId)
    const normalisedAddressId = normaliseId(shippingAddressId)
    if (!normalisedInstituteId || !normalisedAddressId) {
      clearLastUsedAddress()
      return
    }

    const addressData: LastUsedAddressData = {
      shippingAddressId: normalisedAddressId,
      instituteId: normalisedInstituteId,
      timestamp: Date.now(),
      userId
    }
    localStorage.setItem(LAST_USED_ADDRESS_KEY, JSON.stringify(addressData))
  } catch (error) {
    console.warn('Failed to save last used address to localStorage:', error)
  }
}

/**
 * Get the last used delivery address from localStorage for a given institute
 */
export const getLastUsedAddress = (
  instituteId: string | number | null | undefined,
  userId?: string
): string | null => {
  try {
    const normalisedInstituteId = normaliseId(instituteId)
    if (!normalisedInstituteId) {
      return null
    }

    const stored = localStorage.getItem(LAST_USED_ADDRESS_KEY)
    if (!stored) {
      return null
    }

    const addressData: LastUsedAddressData = JSON.parse(stored)

    // If userId is provided, check if it matches (for multi-user scenarios)
    if (userId && addressData.userId && addressData.userId !== userId) {
      return null
    }

    if (normaliseId(addressData.instituteId) !== normalisedInstituteId) {
      return null
    }

    // Check if the data is not too old (optional: 30 days expiry)
    const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000
    if (Date.now() - addressData.timestamp > thirtyDaysInMs) {
      clearLastUsedAddress()
      return null
    }

    return normaliseId(addressData.shippingAddressId)
  } catch (error) {
    console.warn(
      'Failed to retrieve last used address from localStorage:',
      error
    )
    return null
  }
}

/**
 * Resolve a remembered address id against the addresses the current institute
 * accepts. Returns the matching entry from that list, so the caller keeps the
 * runtime type the rest of the checkout compares against, or null when it
 * cannot be confirmed as one of them.
 */
export const resolveRememberedAddressId = (
  candidateId: string | number | null | undefined,
  validAddressIds: ReadonlyArray<string | number | null | undefined>
): string | null => {
  const normalisedCandidate = normaliseId(candidateId)
  if (!normalisedCandidate) {
    return null
  }

  const match = (validAddressIds ?? []).find(
    (id) => normaliseId(id) === normalisedCandidate
  )

  return match === null || match === undefined ? null : (match as string)
}

/**
 * Clear the last used delivery address from localStorage
 * This should only be called when explicitly needed (e.g., user logout, address no longer valid)
 */
export const clearLastUsedAddress = (): void => {
  try {
    localStorage.removeItem(LAST_USED_ADDRESS_KEY)
  } catch (error) {
    console.warn('Failed to clear last used address from localStorage:', error)
  }
}

/**
 * Check if a last used address exists for a given institute
 */
export const hasLastUsedAddress = (
  instituteId: string | number | null | undefined,
  userId?: string
): boolean => {
  return getLastUsedAddress(instituteId, userId) !== null
}
